import type { CopilotScope } from "@productlane/lib/src/ai"

import type { AgentTab } from "./format"
import { isAgentTab } from "./format"

export interface AgentRoute {
	tab: AgentTab
	itemId: string | null
	// Set when the URL is a legacy /agent/:recommendationId link.
	redirect: string | null
}

export function agentPath(tab: AgentTab, itemId?: string | null): string {
	if (itemId) return `/agent/${tab}/${itemId}`
	return tab === "all" ? "/agent" : `/agent/${tab}`
}

const CHAT_TABS = new Set(["new", "chat", "chats"])

/** The ideas list and an idea opened from it, as opposed to a chat or View all. */
export function isRecommendationsPath(pathname: string): boolean {
	const [root, tab, itemId, ...rest] = pathname.split("/").filter(Boolean)
	if (root !== "agent" || rest.length > 0) return false
	if (!tab || isAgentTab(tab)) return true
	// A legacy /agent/:recommendationId link, about to redirect to the idea.
	return !itemId && !CHAT_TABS.has(tab)
}

export function parseAgentRoute(params: {
	tab?: string
	itemId?: string
}): AgentRoute {
	if (!params.tab) return { tab: "all", itemId: null, redirect: null }
	if (isAgentTab(params.tab)) {
		return { tab: params.tab, itemId: params.itemId ?? null, redirect: null }
	}
	// "+" in the sidebar starts a fresh chat; "chat" is an assistant panel expanded to the page.
	if (params.tab === "new" || params.tab === "chat") {
		return { tab: "all", itemId: null, redirect: null }
	}
	// View all: every idea and assistant chat. Old Chats-tab links with a thread id open the list.
	if (params.tab === "chats") {
		return {
			tab: "all",
			itemId: null,
			redirect: params.itemId ? "/agent/chats" : null,
		}
	}
	// Digest emails and old bookmarks point at /agent/:recommendationId.
	return {
		tab: "all",
		itemId: params.tab,
		redirect: agentPath("all", params.tab),
	}
}

// Only the ids go in the URL: a thread title can carry a customer's words.
export function expandedChatPath(scope: CopilotScope): string {
	const ids: Record<string, string> = { kind: scope.kind }
	if (scope.kind === "thread") ids.threadId = scope.threadId
	if (scope.kind === "article") ids.articleId = scope.articleId
	if (scope.kind === "changelog") ids.changelogId = scope.changelogId
	if (scope.kind === "agent") {
		if (scope.recommendationId) ids.recommendationId = scope.recommendationId
		if (scope.chatId) ids.chatId = scope.chatId
	}
	return `/agent/chat?${new URLSearchParams(ids).toString()}`
}

export type CollapsedChatTarget =
	| { kind: "thread"; path: string }
	| { kind: "editor"; path: string }
	| { kind: "inbox"; path: string }

/** Where a full-page chat goes back to a side panel: its own page, else the inbox. */
export function collapsedChatTarget(scope: CopilotScope): CollapsedChatTarget {
	switch (scope.kind) {
		case "thread":
			return { kind: "thread", path: `/threads/${scope.threadId}` }
		case "article":
			return { kind: "editor", path: `/edit-docs/${scope.articleId}` }
		case "changelog":
			return { kind: "editor", path: `/edit-changelog/${scope.changelogId}` }
		default:
			return { kind: "inbox", path: "/threads" }
	}
}

export function parseExpandedScope(search: string): CopilotScope | null {
	const params = new URLSearchParams(search)
	const id = (key: string) => params.get(key) || null
	switch (params.get("kind")) {
		case "thread": {
			const threadId = id("threadId")
			return threadId ? { kind: "thread", threadId } : null
		}
		case "article": {
			const articleId = id("articleId")
			return articleId ? { kind: "article", articleId } : null
		}
		case "changelog": {
			const changelogId = id("changelogId")
			return changelogId ? { kind: "changelog", changelogId } : null
		}
		case "workspace":
			return { kind: "workspace" }
		case "reporting":
			return { kind: "reporting" }
		case "agent": {
			const recommendationId = id("recommendationId")
			const chatId = id("chatId")
			return {
				kind: "agent",
				...(recommendationId ? { recommendationId } : {}),
				...(chatId ? { chatId } : {}),
			}
		}
		default:
			return null
	}
}

export type ContextScope = Extract<
	CopilotScope,
	{ kind: "thread" | "article" | "changelog" }
>

/** The page an expanded chat came from, which stays open beside it. */
export function contextScope(scope: CopilotScope | null): ContextScope | null {
	if (!scope) return null
	if (
		scope.kind === "thread" ||
		scope.kind === "article" ||
		scope.kind === "changelog"
	) {
		return scope
	}
	return null
}
